import {
  DataSourceAutoImportMode,
  DataSourceFigmaScope,
  DataSourceFigmaState,
  DataSourceRemoteType,
  DataSourceStats,
  DataSourceUploadImportMetadata,
  DataSourceUploadRemoteSource,
  SourceImportStorybookSummary,
  SourceImportSummary,
} from "@supernova-studio/model";
import { z } from "zod";

//
// Read
//

export const DTODataSourceFigmaScope = DataSourceFigmaScope;

const DTODataSourceFigmaFileVersion = z.object({
  id: z.string(),
  created_at: z.coerce.date(),
  label: z.string(),
  description: z.string(),
});

export const DTODataSourceFigmaCloud = z.object({
  fileId: z.string(),
  state: DataSourceFigmaState,
  autoImportMode: DataSourceAutoImportMode,
  fileThumbnailUrl: z.string().optional(),
  lastImportResult: SourceImportSummary.nullish(),
  lastImportedAt: z.coerce.date().nullish(),
  lastImportedVersion: DTODataSourceFigmaFileVersion.nullish(),
  lastUpdatesCheckedAt: z.date().nullish(),
  ownerId: z.string(),
  ownerUserName: z.string().optional(),
  preferredCredentialId: z.string().optional(),
  stats: DataSourceStats,
});

export const DTODataSourceFigma = z.object({
  id: z.string(),
  type: z.literal(DataSourceRemoteType.Enum.Figma),
  fileName: z.string(),
  scope: DTODataSourceFigmaScope,
  brandId: z.string(),
  themeId: z.string().nullish(),
  cloud: DTODataSourceFigmaCloud.nullish(),
  sortOrder: z.number().optional(),
});

// TS is not yet implemented in new BE
export const DTODataSourceTokenStudio = z.object({
  id: z.string(),
  type: z.literal(DataSourceRemoteType.Enum.TokenStudio),
  fileName: z.string(),
  brandId: z.string(),
  themeId: z.string().nullish(),
  sortOrder: z.number().optional(),
  tokenStudio: z.object({
    settings: z.object({
      dryRun: z.boolean(),
      verbose: z.boolean(),
      preciseCopy: z.boolean(),
    }),
    connectionName: z.string(),
    lastImportedAt: z.coerce.date(),
    lastImportedResults: z.array(
      z.object({
        mapping: z.object({
          tokenSets: z.array(z.string()),
          supernovaBrand: z.string(),
          supernovaTheme: z.string().optional(),
        }),
        isFailed: z.boolean(),
        tokensCreated: z.number(),
        tokensDeleted: z.number(),
        tokensUpdated: z.number(),
      })
    ),
  }),
});

export const DTODataSourceFigmaVariablesPlugin = z.object({
  id: z.string(),
  type: z.literal(DataSourceRemoteType.Enum.FigmaVariablesPlugin),
  fileName: z.string(),
  brandId: z.string(),
  sortOrder: z.number().optional(),
  upload: z.object({
    remoteId: z.string(),
    remoteSourceType: DataSourceUploadRemoteSource,
    lastImportedAt: z.coerce.date().optional(),
    lastImportMetadata: DataSourceUploadImportMetadata.optional(),
    isTokenTypeSplitEnabled: z.boolean(),
  }),
  figmaVariablesPlugin: z.object({
    fileId: z.string(),
    lastImportedAt: z.coerce.date().optional(),
    lastImportMetadata: DataSourceUploadImportMetadata.optional(),
    isTokenTypeSplitEnabled: z.boolean(),
    isCollectionsMigrationCompleted: z.boolean().default(false),
  }),
});

export const DTODataSourceStorybook = z.object({
  id: z.string(),
  type: z.literal(DataSourceRemoteType.Enum.Storybook),
  fileName: z.string(),
  brandId: z.string(),
  sortOrder: z.number().optional(),
  storybook: z.object({
    indexUrl: z.string(),
    userUrl: z.string(),
    lastImportedAt: z.coerce.date().optional(),
    lastImportedResult: SourceImportStorybookSummary.optional(),
    lastImportedSuccessfully: z.coerce.date().optional(),
    isFailed: z.boolean(),
    storiesCount: z.number(),
    docsCount: z.number(),
    description: z.string().optional(),
    isUsingVpn: z.boolean().optional(),
  }),
});

export const DTODataSourcesStorybookResponse = z.object({
  isFailed: z.boolean(),
  storiesCount: z.number(),
  docsCount: z.number(),
  created: z.array(z.string()).optional(),
  updated: z.array(z.string()).optional(),
  deleted: z.array(z.string()).optional(),
});

export const DTODataSource = z.discriminatedUnion("type", [
  DTODataSourceFigma,
  DTODataSourceFigmaVariablesPlugin,
  DTODataSourceTokenStudio,
  DTODataSourceStorybook,
]);

export const DTODataSourcesListResponse = z.object({
  sources: z.array(DTODataSource),
});

export const DTODataSourceResponse = z.object({
  source: DTODataSource,
});

export type DTODataSourceFigma = z.infer<typeof DTODataSourceFigma>;
export type DTODataSourceTokenStudio = z.infer<typeof DTODataSourceTokenStudio>;
export type DTODataSourceFigmaVariablesPlugin = z.infer<typeof DTODataSourceFigmaVariablesPlugin>;
export type DTODataSourceStorybook = z.infer<typeof DTODataSourceStorybook>;
export type DTODataSource = z.infer<typeof DTODataSource>;
export type DTODataSourceResponse = z.infer<typeof DTODataSourceResponse>;
export type DTODataSourcesListResponse = z.infer<typeof DTODataSourcesListResponse>;
export type DTODataSourcesStorybookResponse = z.infer<typeof DTODataSourcesStorybookResponse>;

//
// Write
//

export const DTODataSourceFigmaCreatePayload = z.object({
  brandPersistentId: z.string(),
  figmaFileUrl: z.string(),
  scope: DTODataSourceFigmaScope,
  autoImportMode: DataSourceAutoImportMode,
});

export const DTODataSourceFigmaImportPayload = z.object({
  sourceIds: z.array(z.string()),
});

export const DTODataSourceStorybookCreatePayload = z.object({
  brandPersistentId: z.string(),
  fileName: z.string().optional(),
  userUrl: z.string(),
  indexUrl: z.string(),
  payload: z.unknown(),
  type: z.literal(DataSourceRemoteType.Enum.Storybook),
  description: z.string().optional(),
  isUsingVpn: z.boolean().optional(),
});

export const DTOStorybookImportPayload = z.object({
  sourceId: z.string(),
  payload: z.unknown(),
});

export const DTOStorybookSourceUpdatePayload = z.object({
  description: z.string().nullish().optional(),
  isUsingVpn: z.boolean().optional(),
  fileName: z.string().optional(),
});

export const DTOFigmaSourceUpdatePayload = z.object({
  themeId: z.string().nullish(),
  scope: DataSourceFigmaScope.optional(),
  autoImportMode: DataSourceAutoImportMode.optional(),
});

export type DTODataSourceFigmaScope = z.infer<typeof DTODataSourceFigmaScope>;
export type DTODataSourceFigmaCreatePayload = z.infer<typeof DTODataSourceFigmaCreatePayload>;
export type DTODataSourceStorybookCreatePayload = z.infer<typeof DTODataSourceStorybookCreatePayload>;
export type DTODataSourceFigmaImportPayload = z.infer<typeof DTODataSourceFigmaImportPayload>;
export type DTOStorybookImportPayload = z.infer<typeof DTOStorybookImportPayload>;
export type DTOStorybookSourceUpdatePayload = z.infer<typeof DTOStorybookSourceUpdatePayload>;
export type DTOFigmaSourceUpdatePayload = z.infer<typeof DTOFigmaSourceUpdatePayload>;
